import { useState } from 'react';
import { authService } from '../../lib/api-service';
import { setToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router';

export default function ChangePassword() {
  const { user } = useAuth();
  const hasPassword = user?.has_usable_password !== false;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Parolele noi nu coincid.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await authService.changePassword(
        hasPassword ? currentPassword : undefined,
        newPassword,
      );
      setToken(result.token);
      setSuccess(true);
    } catch {
      setError(hasPassword
        ? 'Parola curentă este incorectă sau parola nouă nu îndeplinește cerințele.'
        : 'Parola nouă nu îndeplinește cerințele (minim 8 caractere, literă mare, literă mică, cifră).');
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    'w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20';

  if (success) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
        <h2 className="mb-2 text-xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          Parola a fost schimbată
        </h2>
        <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Noua parolă este activă imediat.
        </p>
        <Link
          to="/staff"
          className="inline-block rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          Înapoi la dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <div className="mb-6 flex items-center gap-3">
        <Link to="/staff" className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          Schimbă parola
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="rounded-2xl border bg-white p-6 shadow-sm">
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {hasPassword && (
          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
              Parola curentă
            </label>
            <input
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={inputClass}
              style={{ fontFamily: 'var(--font-secondary)' }}
            />
          </div>
        )}

        {!hasPassword && (
          <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
            Nu ai o parolă setată. Completează câmpurile de mai jos pentru a seta una.
          </div>
        )}

        <div className="mb-4">
          <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
            Parola nouă
          </label>
          <input
            type="password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputClass}
            style={{ fontFamily: 'var(--font-secondary)' }}
          />
          <p className="mt-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Minim 8 caractere, o literă mare, o literă mică, o cifră.
          </p>
        </div>

        <div className="mb-6">
          <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
            Confirmă parola nouă
          </label>
          <input
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClass}
            style={{ fontFamily: 'var(--font-secondary)' }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-[var(--vivid-purple)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          {submitting ? 'Se schimbă...' : 'Schimbă parola'}
        </button>
      </form>
    </div>
  );
}
